// shell/components/data-list-view/data-list-view.ts

import { IDisposable, RowState, SelectionState, TimeoutHandle } from '../types';
import { DisposableStore } from '../disposable';
import { VirtualScroller } from '../virtual-scroller';
import { SelectionManager } from '../selection';
import { DataSource, LiveDataSource } from '../data-source';
import { ColumnManager, ColumnRendererFactory, ColumnDef } from '../table';
import { TableRenderer } from '../table/table-renderer';
import { GridRenderer } from '../grid/grid-renderer';
import { IRenderer } from '../renderer-types';
import { HeatMapBounds } from '../heat-map';
import { RowStateManager } from '../row-state';
import { BulkActionBar } from '../bulk-actions';
import {
    DataListViewConfig,
    DataListViewState,
    ViewMode,
    PersistedViewState
} from './types';
import { loadViewState, saveViewState, localStorageProvider } from './persistence';
import { pluralizeCount } from '../../utils/format';

/**
 * DataListView orchestrates all list components into a cohesive experience.
 *
 * Component ownership:
 * - DataListView creates and owns: VirtualScroller, SelectionManager, ColumnManager,
 *   ColumnRendererFactory, TableRenderer, GridRenderer, RowStateManager, BulkActionBar
 * - DataListView receives but does not own: DataSource (passed in config)
 * - HeatMapShader is owned by ColumnRendererFactory (per Phase 2 design)
 *
 * Data flow:
 * 1. DataSource emits 'change' → DataListView updates scroller totalCount
 * 2. VirtualScroller emits 'onRangeChange' → DataListView ensures data loaded
 * 3. VirtualScroller emits 'onRender' → DataListView fetches items, passes to renderer
 * 4. Renderer builds DOM → SelectionManager state applied via isSelected callback
 * 5. User clicks row → Renderer emits event → DataListView updates SelectionManager
 * 6. SelectionManager emits 'change' → DataListView updates BulkActionBar
 * 7. LiveDataSource emits changes → RowStateManager marks fresh/deleted
 */
export class DataListView<T> implements IDisposable {
    // === Configuration ===
    private readonly config: DataListViewConfig<T>;
    private readonly keyFn: (item: T) => string;

    // === Owned Components ===
    private readonly disposables = new DisposableStore();
    private readonly dataSource: DataSource<T> | LiveDataSource<T>;
    private scroller!: VirtualScroller;
    private readonly selectionManager: SelectionManager<T>;
    private readonly columnManager: ColumnManager<T>;
    private readonly rendererFactory: ColumnRendererFactory<T>;
    private readonly rowStateManager: RowStateManager;
    private readonly bulkActionBar: BulkActionBar<T> | null;

    // === Renderers (swapped on mode change) ===
    private tableRenderer: TableRenderer<T> | null = null;
    private gridRenderer: GridRenderer<T> | null = null;
    private activeRenderer: IRenderer<T> | null = null;

    // === DOM Elements ===
    private readonly rootElement: HTMLElement;
    private readonly scrollContainer: HTMLElement;
    private readonly headerContainer: HTMLElement | null;
    private readonly bulkActionsContainer: HTMLElement | null;

    // === State ===
    private state: DataListViewState<T>;
    private readonly persistenceKey: string | null;
    private persistenceDebounceId: TimeoutHandle | null = null;
    private pendingScrollRestore: number | null = null;
    private _activeKey: string | null = null;

    constructor(config: DataListViewConfig<T>) {
        this.config = config;
        this.dataSource = config.dataSource;
        this.keyFn = config.keyFn ?? config.dataSource.getKeyFn();

        // Initialize state with defaults
        this.state = this.initializeState(config);
        this.persistenceKey = config.persistenceKey ?? null;

        // Restore persisted state BEFORE creating renderer
        // This ensures we create the correct renderer type on first pass
        this.restorePersistedStateToState();

        // Create root DOM structure
        // Order matters: header must be created before scroll container for correct DOM order
        this.rootElement = this.createRootElement(config.container);
        this.headerContainer = config.stickyHeader !== false ? this.createHeaderContainer() : null;
        this.scrollContainer = this.createScrollContainer();
        this.bulkActionsContainer = config.selectionMode === 'multi' ? this.createBulkActionsContainer() : null;

        // Create ColumnManager
        this.columnManager = new ColumnManager({
            columns: config.columns,
            persistenceKey: config.persistenceKey ? `${config.persistenceKey}_columns` : undefined,
            onVisibilityChange: () => this.onColumnsChanged(),
            onOrderChange: () => this.onColumnsChanged()
        });
        this.disposables.add(this.columnManager);

        // Create ColumnRendererFactory
        // Note: Factory owns its own HeatMapShader instance (per Phase 2 design)
        this.rendererFactory = new ColumnRendererFactory<T>();
        this.disposables.add(this.rendererFactory);

        // Create RowStateManager
        this.rowStateManager = new RowStateManager({
            onStateChange: (key, state) => this.onRowStateChange(key, state),
            onDeletedExpired: (key) => this.onDeletedExpired(key)
        });
        this.disposables.add(this.rowStateManager);

        // Create SelectionManager
        this.selectionManager = new SelectionManager({
            keyFn: this.keyFn,
            mode: config.selectionMode ?? 'none',
            selectAllBehavior: config.selectAllBehavior ?? 'loaded',
            canSelect: config.canSelect,
            getTotalCount: () => this.dataSource.getTotalCount(),
            getItemByKey: (key) => this.dataSource.getItemByKey(key),
            onSelectionChange: (state) => this.onSelectionChange(state)
        });
        this.disposables.add(this.selectionManager);

        // Create VirtualScroller
        this.createVirtualScroller();

        // Create BulkActionBar (if multi-selection enabled)
        if (this.bulkActionsContainer && config.bulkActions && config.bulkActions.length > 0) {
            this.bulkActionBar = new BulkActionBar({
                container: this.bulkActionsContainer,
                actions: config.bulkActions,
                onClearSelection: () => this.selectionManager.clear()
            });
            this.disposables.add(this.bulkActionBar);
        } else {
            this.bulkActionBar = null;
        }

        // Create initial renderer (uses restored state.viewMode)
        this.createRenderer(this.state.viewMode);

        // Setup event listeners
        this.setupDataSourceListeners();
        this.setupKeyboardNavigation();

        // Start live data if applicable
        if (this.isLiveDataSource(this.dataSource)) {
            this.dataSource.startListening();
        }

        // Apply initial sort if persisted
        if (this.state.sortColumn) {
            this.dataSource.setSort(this.state.sortColumn, this.state.sortDirection);
        }

        // Trigger initial data load (ensures data loads even if totalCount starts at 0)
        // Loading index 0 will fetch the first page, which updates totalCount
        this.dataSource.ensureLoaded(0, 0);
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Restore persisted state into this.state (without creating renderer).
     * Called during construction BEFORE renderer creation.
     */
    private restorePersistedStateToState(): void {
        if (!this.persistenceKey) return;

        const storage = this.config.storage ?? localStorageProvider;
        const saved = loadViewState(this.persistenceKey, storage);

        if (saved) {
            if (saved.viewMode && this.isValidViewMode(saved.viewMode)) {
                this.state.viewMode = saved.viewMode;
            }
            if (saved.thumbSize !== undefined) {
                this.state.thumbSize = saved.thumbSize;
            }
            if (saved.sortColumn) {
                this.state.sortColumn = saved.sortColumn;
                this.state.sortDirection = saved.sortDirection ?? 'asc';
            }
            if (saved.heatMapEnabled !== undefined) {
                this.state.heatMapEnabled = saved.heatMapEnabled;
            }
            if (saved.scrollTop !== undefined && saved.scrollTop > 0) {
                this.pendingScrollRestore = saved.scrollTop;
                console.log('[DLV] restored pendingScrollRestore:', saved.scrollTop);
            }
        }
    }

    private initializeState(config: DataListViewConfig<T>): DataListViewState<T> {
        const sort = this.dataSource.getSort();
        return {
            viewMode: config.initialMode ?? 'table',
            thumbSize: config.thumbSizes?.default ?? 150,
            sortColumn: sort?.column ?? null,
            sortDirection: sort?.direction ?? 'asc',
            heatMapEnabled: config.enableHeatMap ?? false,
            isLoading: false,
            error: null,
            renderedStartIndex: 0,
            renderedEndIndex: 0,
            focusedItemKey: null
        };
    }

    private isLiveDataSource(ds: DataSource<T> | LiveDataSource<T>): ds is LiveDataSource<T> {
        return 'startListening' in ds;
    }

    // ========================================================================
    // DOM STRUCTURE
    // ========================================================================

    private get isNonInteractive(): boolean {
        return !this.config.onItemSelect && this.config.selectionMode !== 'multi';
    }

    private createRootElement(container: HTMLElement): HTMLElement {
        const root = document.createElement('div');
        root.className = `cs-data-list-view cs-data-list-view--${this.state.viewMode}`;
        if (this.isNonInteractive) {
            root.classList.add('cs-data-list-view--non-interactive');
        }
        root.setAttribute('role', 'region');
        root.setAttribute('aria-label', this.config.ariaLabel);
        container.appendChild(root);
        return root;
    }

    private createHeaderContainer(): HTMLElement {
        const header = document.createElement('div');
        header.className = 'cs-data-list-view__header';
        this.rootElement.appendChild(header);
        return header;
    }

    private createScrollContainer(): HTMLElement {
        const scroll = document.createElement('div');
        scroll.className = 'cs-data-list-view__scroll';
        this.rootElement.appendChild(scroll);
        return scroll;
    }

    private createBulkActionsContainer(): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'cs-data-list-view__bulk-actions';
        bar.setAttribute('aria-hidden', 'true');
        this.rootElement.appendChild(bar);
        return bar;
    }

    private createEmptyState(): HTMLElement {
        const empty = document.createElement('div');
        empty.className = 'cs-data-list-view__empty';
        const message = document.createElement('div');
        message.className = 'cs-data-list-view__empty-message';
        message.textContent = `${pluralizeCount(this.config.itemType ?? 'item', 0)} to display`;
        empty.appendChild(message);
        return empty;
    }

    // ========================================================================
    // VIRTUAL SCROLLER
    // ========================================================================

    private createVirtualScroller(): void {
        this.scroller = new VirtualScroller({
            container: this.scrollContainer,
            estimatedRowHeight: this.getEstimatedRowHeight(),
            bufferRows: 5,
            totalCount: this.dataSource.getTotalCount(),
            role: this.state.viewMode === 'table' ? 'grid' : 'listbox',
            ariaLabel: this.config.ariaLabel,
            announcePosition: this.config.announcePosition,
            // Only apply rowGap in grid/thumbs mode - table rows don't have gaps
            rowGap: this.state.viewMode === 'table' ? 0 : (this.config.gridGap ?? 8),
            getColumnsPerRow: () => this.getColumnsPerRow(),
            onRender: (container, startIndex, endIndex) => this.handleRender(container, startIndex, endIndex),
            onRangeChange: (startIndex, endIndex) => this.handleRangeChange(startIndex, endIndex)
        });
        this.disposables.add(this.scroller);
    }

    // ========================================================================
    // RENDERER MANAGEMENT
    // ========================================================================

    private createRenderer(mode: ViewMode): void {
        // Dispose existing renderer
        this.disposeActiveRenderer();

        if (mode === 'table') {
            this.tableRenderer = this.createTableRenderer();
            this.activeRenderer = this.tableRenderer;

            // Render header
            if (this.headerContainer) {
                this.headerContainer.innerHTML = '';
                this.tableRenderer.renderHeader(this.headerContainer);
                this.headerContainer.classList.remove('cs-data-list-view__header--hidden');
            }
        } else {
            // Grid or thumbs mode
            this.gridRenderer = this.createGridRenderer(mode);
            this.activeRenderer = this.gridRenderer;

            // Set CSS variables for grid layout
            const isThumbs = mode === 'thumbs';
            const cardWidth = isThumbs ? this.state.thumbSize : (this.config.cardWidth ?? 200);
            const gap = this.config.gridGap ?? 8;
            this.rootElement.style.setProperty('--cs-card-width', `${cardWidth}px`);
            this.rootElement.style.setProperty('--cs-grid-gap', `${gap}px`);

            // Hide header in grid mode
            if (this.headerContainer) {
                this.headerContainer.classList.add('cs-data-list-view__header--hidden');
            }
        }

        // Update root class
        this.rootElement.className = `cs-data-list-view cs-data-list-view--${mode}`;
        if (this.isNonInteractive) {
            this.rootElement.classList.add('cs-data-list-view--non-interactive');
        }
        if (this.config.stretchCards) {
            this.rootElement.classList.add('cs-data-list-view--stretch');
        }
        if (this.state.isLoading) {
            this.rootElement.classList.add('cs-data-list-view--loading');
        }

        // Update scroller configuration
        this.scroller.setTotalCount(this.dataSource.getTotalCount());
        this.scroller.refresh();
    }

    private createTableRenderer(): TableRenderer<T> {
        return new TableRenderer({
            rootElement: this.rootElement,
            columnManager: this.columnManager,
            rendererFactory: this.rendererFactory,
            getRowId: this.keyFn,
            getRowState: (item) => this.rowStateManager.getState(this.keyFn(item)),
            rowHeight: this.config.tableRowHeight ?? 35,
            totalCount: this.dataSource.getTotalCount(),
            selectable: this.config.selectionMode === 'multi',
            isSelected: (item) => this.selectionManager.isSelected(item),
            isActive: (item) => this.keyFn(item) === this._activeKey,
            onSelectionToggle: (item, event) => this.handleSelectionToggle(item, event),
            onRowClick: (item, index, event) => this.handleRowClick(item, index, event),
            onRowDoubleClick: (item, index, event) => this.handleRowDoubleClick(item, index, event),
            onHeaderClick: (columnKey, event) => this.handleHeaderClick(columnKey, event),
            onKeyDown: (event, item) => this.handleRendererKeyDown(event, item),
            sortColumn: this.state.sortColumn ?? undefined,
            sortDirection: this.state.sortDirection
        });
    }

    private createGridRenderer(mode: ViewMode): GridRenderer<T> {
        const isThumbs = mode === 'thumbs';
        const cardSize = isThumbs ? this.state.thumbSize : (this.config.cardWidth ?? 200);
        // For thumbs mode, use thumb size as minCardWidth for proper column calculation
        const minCardWidth = isThumbs ? this.state.thumbSize : (this.config.minCardWidth ?? 200);

        return new GridRenderer({
            columnManager: this.columnManager,
            rendererFactory: this.rendererFactory,

            // Card sizing
            cardHeight: cardSize,
            minCardWidth: minCardWidth,
            gap: this.config.gridGap ?? 8,

            // Item identification
            getCardId: this.keyFn,
            getCardState: (item) => this.rowStateManager.getState(this.keyFn(item)),

            // Selection
            selectable: this.config.selectionMode === 'multi',
            isSelected: (item) => this.selectionManager.isSelected(item),
            isActive: (item) => this.keyFn(item) === this._activeKey,
            onSelectionToggle: (item, event) => this.handleSelectionToggle(item, event),

            // Custom card rendering - adapt signature from (item, element) to (element, item, index)
            onCardRender: this.config.cardRenderer
                ? (element, item, _index) => this.config.cardRenderer!(item, element)
                : undefined,

            // Events
            onCardClick: (item, index, event) => this.handleCardClick(item, index, event),
            onCardDoubleClick: (item, index, event) => this.handleCardDoubleClick(item, index, event),
            onKeyDown: (event, item) => this.handleRendererKeyDown(event, item)
        });
    }

    private disposeActiveRenderer(): void {
        if (this.tableRenderer) {
            this.tableRenderer.dispose();
            this.tableRenderer = null;
        }
        if (this.gridRenderer) {
            this.gridRenderer.dispose();
            this.gridRenderer = null;
        }
        this.activeRenderer = null;
    }

    // ========================================================================
    // RENDER PIPELINE
    // ========================================================================

    /**
     * Called by VirtualScroller when visible range changes.
     */
    private handleRender(container: HTMLElement, startIndex: number, endIndex: number): void {
        // Check for empty state
        if (this.dataSource.getTotalCount() === 0) {
            // If data is still loading, keep showing current content to avoid
            // a flash of empty state during filter changes / refreshes
            if (this.dataSource.isLoading()) {
                return;
            }
            container.innerHTML = '';
            // Clear renderer state since we're clearing the container
            if (this.activeRenderer) {
                this.activeRenderer.invalidate();
            }
            container.appendChild(this.createEmptyState());
            return;
        }

        // Clear empty state if it was previously shown
        const emptyState = container.querySelector('.cs-data-list-view__empty');
        if (emptyState) {
            emptyState.remove();
        }

        // Get items for range
        const items: T[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
            const item = this.dataSource.getItem(i);
            if (item) {
                items.push(item);
            }
        }

        // Delegate to active renderer with error boundary
        if (this.activeRenderer) {
            try {
                this.activeRenderer.render(container, items, startIndex);

                // Tell scroller to observe rendered elements for height tracking
                const elements = this.activeRenderer.getRenderedElements();
                if (elements.length > 0) {
                    this.scroller.observeRenderedElements(elements);
                }
            } catch (error) {
                console.error('Render error:', error);
                this.showRenderError(container, error as Error);
                return;
            }
        }

        // Update state
        this.state.renderedStartIndex = startIndex;
        this.state.renderedEndIndex = endIndex;

        // Compute heat map bounds for visible range
        if (this.state.heatMapEnabled) {
            this.updateHeatMapBounds();
        }
    }

    /**
     * Show error state when rendering fails.
     */
    private showRenderError(container: HTMLElement, error: Error): void {
        container.innerHTML = '';
        const errorEl = document.createElement('div');
        errorEl.className = 'cs-data-list-view__render-error';
        errorEl.textContent = `Render error: ${error.message}`;
        container.appendChild(errorEl);
    }

    /**
     * Called by VirtualScroller when range changes (may need data loading).
     */
    private handleRangeChange(startIndex: number, endIndex: number): void {
        // Ensure data is loaded for the range
        // Note: Don't call refresh() here - it causes an infinite loop:
        //   render → onRangeChange → handleRangeChange → refresh → render → ...
        // The DataSource.onDataChange handler will trigger re-render when new data arrives.
        this.dataSource.ensureLoaded(startIndex, endIndex)
            .catch((error) => {
                this.handleError(error);
            });

        // Persist scroll position (debounced with other view state)
        this.persistState();
    }

    /**
     * Compute heat map bounds for current visible range.
     * Uses ColumnManager.computeHeatMapBoundsFromDataSource() per Phase 2 design.
     */
    private updateHeatMapBounds(): void {
        // Notify ColumnManager that data changed (invalidates cache)
        this.columnManager.notifyDataChanged();

        // Compute bounds via ColumnManager (which owns the logic and stores in cache)
        this.columnManager.computeHeatMapBoundsFromDataSource(
            this.dataSource,
            this.state.renderedStartIndex,
            this.state.renderedEndIndex
        );

        // Build bounds map from cache for renderer
        const bounds = new Map<string, HeatMapBounds>();
        for (const column of this.columnManager.getVisibleColumns()) {
            if (column.heatMap?.enabled) {
                const columnBounds = this.columnManager.getHeatMapBounds(column.key);
                if (columnBounds) {
                    bounds.set(column.key, columnBounds);
                }
            }
        }

        // Update renderer with bounds
        if (this.activeRenderer) {
            this.activeRenderer.updateHeatMap(bounds);
        }
    }

    // ========================================================================
    // EVENT HANDLERS - DATA SOURCE
    // ========================================================================

    private setupDataSourceListeners(): void {
        this.disposables.add(
            this.dataSource.on('change', () => this.onDataChange())
        );
        this.disposables.add(
            this.dataSource.on('loading', (loading: unknown) => this.onLoadingChange(loading as boolean))
        );
        this.disposables.add(
            this.dataSource.on('error', (error: unknown) => this.handleError(error as Error))
        );
        this.disposables.add(
            this.dataSource.on('beforeRefresh', () => this.onBeforeRefresh())
        );
        this.disposables.add(
            this.dataSource.on('afterRefresh', () => this.onAfterRefresh())
        );
    }

    private onDataChange(): void {
        // Update scroller total count
        this.scroller.setTotalCount(this.dataSource.getTotalCount());

        // Update renderer total count
        if (this.activeRenderer) {
            this.activeRenderer.setTotalCount(this.dataSource.getTotalCount());
        }

        // Restore scroll position on first data load.
        // Must sync scrollable height first — the deferred render() hasn't run yet,
        // so the scrollable area is still 0-height and the browser would clamp scrollTop to 0.
        if (this.pendingScrollRestore !== null) {
            const scrollTop = this.pendingScrollRestore;
            this.pendingScrollRestore = null;
            this.scroller.syncScrollableHeight();
            this.scroller.setScrollTop(scrollTop);
        }

        // Refresh scroller (will re-render visible range via rAF)
        this.scroller.refresh();
    }

    private onLoadingChange(loading: boolean): void {
        this.state.isLoading = loading;
        this.rootElement.classList.toggle('cs-data-list-view--loading', loading);
        this.config.onLoadingChange?.(loading);
    }

    private onBeforeRefresh(): void {
        // Save focused item key for scroll restoration after refresh
        const focusedIndex = this.scroller.getFocusedIndex();
        if (focusedIndex >= 0) {
            const item = this.dataSource.getItem(focusedIndex);
            if (item) {
                this.state.focusedItemKey = this.keyFn(item);
            }
        }
    }

    private onAfterRefresh(): void {
        // Restore scroll position by finding the previously focused item
        if (this.state.focusedItemKey) {
            const newIndex = this.dataSource.getIndexByKey(this.state.focusedItemKey);
            if (newIndex >= 0) {
                this.scroller.scrollToIndex(newIndex, 'center');
                this.scroller.setFocusedIndex(newIndex);
            }
            this.state.focusedItemKey = null;
        }

        // Reconcile selection with new data
        this.reconcileSelection();
    }

    // ========================================================================
    // EVENT HANDLERS - SELECTION
    // ========================================================================

    private onSelectionChange(state: SelectionState<T>): void {
        // Update bulk action bar
        if (this.bulkActionBar) {
            if (state.mode === 'none') {
                this.bulkActionBar.hide();
            } else if (state.mode === 'explicit') {
                this.bulkActionBar.setSelectedItems(state.items);
                this.bulkActionBar.show();
            } else if (state.mode === 'all') {
                // For 'all' mode, show count-based UI
                // BulkActionBar handles this via setSelectedItems with count info
                this.bulkActionBar.show();
            }
        }

        // Update bulk actions container visibility
        if (this.bulkActionsContainer) {
            this.bulkActionsContainer.setAttribute(
                'aria-hidden',
                state.mode === 'none' ? 'true' : 'false'
            );
        }

        // Schedule update to refresh selection visual (lighter than refresh)
        this.scroller.scheduleUpdate();

        // Emit event
        this.config.onSelectionChange?.(state);
    }

    /**
     * Reconcile selection after data refresh.
     * Uses delta-based approach: only processes currently selected items,
     * not the entire dataset (O(selected) instead of O(total)).
     */
    private reconcileSelection(): void {
        const selectedKeys = this.selectionManager.getSelectedKeys();
        if (selectedKeys.size === 0) return;

        // Check which selected items still exist
        const stillExistingKeys = new Set<string>();
        const updatedItems = new Map<string, T>();

        for (const key of selectedKeys) {
            const item = this.dataSource.getItemByKey(key);
            if (item) {
                stillExistingKeys.add(key);
                updatedItems.set(key, item);
            }
        }

        // Only reconcile if some items were removed
        if (stillExistingKeys.size !== selectedKeys.size) {
            this.selectionManager.reconcile(stillExistingKeys, updatedItems);
        }
    }

    // ========================================================================
    // EVENT HANDLERS - ROW STATE
    // ========================================================================

    private onRowStateChange(key: string, state: RowState): void {
        // Find index for key and update rendered row
        const index = this.dataSource.getIndexByKey(key);
        if (index >= 0 && this.activeRenderer) {
            const item = this.dataSource.getItemByKey(key);
            if (item) {
                this.activeRenderer.updateItem(index, item);
            }
        }
    }

    private onDeletedExpired(key: string): void {
        // Item's delete animation has completed
        // LiveDataSource should have already removed it
        this.scroller.refresh();
    }

    // ========================================================================
    // EVENT HANDLERS - USER INTERACTION
    // ========================================================================

    private handleSelectionToggle(item: T, event: MouseEvent): void {
        if (event.shiftKey && this.selectionManager.getAnchorKey()) {
            // Range selection
            const anchor = this.selectionManager.getAnchor();
            if (anchor) {
                const visibleItems = this.getVisibleItems();
                this.selectionManager.selectRange(anchor, item, visibleItems);
            }
        } else {
            // Toggle selection (checkbox click always toggles)
            this.selectionManager.toggle(item);
        }

        this.selectionManager.setAnchor(item);
    }

    private handleRowClick(item: T, index: number, event: MouseEvent): void {
        this.scroller.setFocusedIndex(index);
        // Set active row for master-detail highlight (only when onItemSelect is configured)
        if (this.config.onItemSelect) {
            this.setActiveKey(this.keyFn(item));
            this.config.onItemSelect(item);
        }
    }

    private handleRowDoubleClick(item: T, index: number, event: MouseEvent): void {
        this.config.onItemActivate?.(item);
    }

    private handleCardClick(item: T, index: number, event: MouseEvent): void {
        this.scroller.setFocusedIndex(index);
        // Set active card for master-detail highlight (only when onItemSelect is configured)
        if (this.config.onItemSelect) {
            this.setActiveKey(this.keyFn(item));
            this.config.onItemSelect(item);
        }
    }

    private handleCardDoubleClick(item: T, index: number, event: MouseEvent): void {
        this.config.onItemActivate?.(item);
    }

    private handleHeaderClick(columnKey: string, event: MouseEvent): void {
        const column = this.columnManager.getColumnByKey(columnKey);
        if (!column?.sortable) return;

        const sortKey = column.sortKey ?? column.key;
        const newDirection: 'asc' | 'desc' =
            this.state.sortColumn === sortKey && this.state.sortDirection === 'asc'
                ? 'desc'
                : 'asc';

        this.setSortColumn(sortKey, newDirection);
    }

    private handleError(error: Error): void {
        this.state.error = error;
        this.config.onError?.(error);
    }

    private handleRendererKeyDown(event: KeyboardEvent, item: T | null): boolean {
        // Additional handling if needed
        return false;
    }

    // ========================================================================
    // KEYBOARD NAVIGATION
    // ========================================================================

    private setupKeyboardNavigation(): void {
        const handleKeyDown = (event: KeyboardEvent): void => {
            // Let selection manager handle selection keys first
            const focusedIndex = this.scroller.getFocusedIndex();
            const focusedItem = this.dataSource.getItem(focusedIndex);

            if (focusedItem) {
                const visibleItems = this.getVisibleItems();
                if (this.selectionManager.handleKeyDown(event, focusedItem, visibleItems)) {
                    this.scroller.scheduleUpdate();
                    return;
                }
            }

            // The per-row keydown handler fires before this (event bubbles).
            // Check if it already moved focus to avoid double-navigation.
            if (this.activeRenderer) {
                const rendererFocusedNow = this.activeRenderer.getFocusedIndex();
                if (rendererFocusedNow >= 0 && rendererFocusedNow !== focusedIndex) {
                    // Per-row handler already moved focus — just sync the scroller
                    event.preventDefault();
                    this.scroller.setFocusedIndex(rendererFocusedNow);
                } else {
                    // Focus didn't move yet — handle scrolling to off-screen items
                    this.handleScrollingNavigation(event);
                }

                // Arrow navigation updates active row and notifies listener
                if (this.config.onItemSelect) {
                    const newFocusedIndex = this.scroller.getFocusedIndex();
                    if (newFocusedIndex !== focusedIndex) {
                        const newItem = this.dataSource.getItem(newFocusedIndex);
                        if (newItem) {
                            this.setActiveKey(this.keyFn(newItem));
                            this.config.onItemSelect(newItem);
                        }
                    }
                }
            }
        };

        this.rootElement.addEventListener('keydown', handleKeyDown);
        this.disposables.add(() => {
            this.rootElement.removeEventListener('keydown', handleKeyDown);
        });
    }

    private handleScrollingNavigation(event: KeyboardEvent): void {
        const totalCount = this.dataSource.getTotalCount();
        const focusedIndex = this.scroller.getFocusedIndex();
        let newIndex = focusedIndex;

        switch (event.key) {
            case 'ArrowDown':
                newIndex = Math.min(focusedIndex + this.getColumnsPerRow(), totalCount - 1);
                break;
            case 'ArrowUp':
                newIndex = Math.max(focusedIndex - this.getColumnsPerRow(), 0);
                break;
            case 'ArrowRight':
                if (this.state.viewMode !== 'table') {
                    newIndex = Math.min(focusedIndex + 1, totalCount - 1);
                }
                break;
            case 'ArrowLeft':
                if (this.state.viewMode !== 'table') {
                    newIndex = Math.max(focusedIndex - 1, 0);
                }
                break;
            case 'Home':
                newIndex = event.ctrlKey ? 0 : focusedIndex;
                break;
            case 'End':
                newIndex = event.ctrlKey ? totalCount - 1 : focusedIndex;
                break;
            case 'PageDown':
                newIndex = Math.min(focusedIndex + this.getPageSize(), totalCount - 1);
                break;
            case 'PageUp':
                newIndex = Math.max(focusedIndex - this.getPageSize(), 0);
                break;
            case 'Enter':
                if (focusedIndex >= 0) {
                    const item = this.dataSource.getItem(focusedIndex);
                    if (item) {
                        event.preventDefault();
                        this.config.onItemActivate?.(item);
                    }
                }
                return;
            default:
                return;
        }

        if (newIndex !== focusedIndex) {
            event.preventDefault();
            this.scroller.setFocusedIndex(newIndex);
            this.scroller.scrollToIndex(newIndex, 'center');
        }
    }

    private getPageSize(): number {
        const viewportHeight = this.scrollContainer.clientHeight;
        const rowHeight = this.getEstimatedRowHeight();
        return Math.floor(viewportHeight / rowHeight) * this.getColumnsPerRow();
    }

    // ========================================================================
    // PUBLIC API - VIEW MODE
    // ========================================================================

    /**
     * Switch view mode (table/grid/thumbs).
     * Triggers smooth transition animation.
     */
    public setViewMode(mode: ViewMode): void {
        if (!this.isValidViewMode(mode)) {
            console.warn(`View mode '${mode}' is not enabled`);
            return;
        }

        if (this.state.viewMode === mode) return;

        // Add transition class
        this.rootElement.classList.add('cs-data-list-view--transitioning');

        // Update state
        this.state.viewMode = mode;

        // Update VirtualScroller settings for new view mode
        // Table mode has no row gap, grid/thumbs modes use gridGap
        this.scroller.setRowGap(mode === 'table' ? 0 : (this.config.gridGap ?? 8));
        this.scroller.setEstimatedRowHeight(this.getEstimatedRowHeight());

        // Create new renderer
        this.createRenderer(mode);

        // Remove transition class after animation
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                this.rootElement.classList.remove('cs-data-list-view--transitioning');
            });
        });

        // Persist and emit
        this.persistState();
        this.config.onViewModeChange?.(mode);
    }

    public getViewMode(): ViewMode {
        return this.state.viewMode;
    }

    // ========================================================================
    // PUBLIC API - THUMB SIZE
    // ========================================================================

    /**
     * Set thumb size for thumbs mode.
     */
    public setThumbSize(size: number): void {
        const min = this.config.thumbSizes?.min ?? 80;
        const max = this.config.thumbSizes?.max ?? 300;
        const clamped = Math.max(min, Math.min(max, size));

        if (this.state.thumbSize === clamped) return;

        this.state.thumbSize = clamped;

        // Update grid renderer if in thumbs mode
        if (this.state.viewMode === 'thumbs' && this.gridRenderer) {
            this.gridRenderer.setCardSize(clamped);
            // Update CSS variable for card width
            this.rootElement.style.setProperty('--cs-card-width', `${clamped}px`);
            this.scroller.refresh();
        }

        this.persistState();
    }

    public getThumbSize(): number {
        return this.state.thumbSize;
    }

    // ========================================================================
    // PUBLIC API - SORTING
    // ========================================================================

    /**
     * Set sort column and direction.
     */
    public async setSortColumn(column: string, direction: 'asc' | 'desc'): Promise<void> {
        this.state.sortColumn = column;
        this.state.sortDirection = direction;

        // Update renderer
        if (this.tableRenderer) {
            this.tableRenderer.setSortColumn(column, direction);
        }

        // Update data source (triggers refresh)
        await this.dataSource.setSort(column, direction);

        this.persistState();
        this.config.onSortChange?.(column, direction);
    }

    public getSort(): { column: string; direction: 'asc' | 'desc' } | null {
        if (!this.state.sortColumn) return null;
        return { column: this.state.sortColumn, direction: this.state.sortDirection };
    }

    // ========================================================================
    // PUBLIC API - HEAT MAP
    // ========================================================================

    /**
     * Enable or disable heat map shading.
     */
    public setHeatMapEnabled(enabled: boolean): void {
        if (this.state.heatMapEnabled === enabled) return;

        this.state.heatMapEnabled = enabled;

        if (enabled) {
            this.updateHeatMapBounds();
        } else {
            if (this.activeRenderer) {
                this.activeRenderer.updateHeatMap(new Map());
            }
        }

        this.persistState();
    }

    public isHeatMapEnabled(): boolean {
        return this.state.heatMapEnabled;
    }

    // ========================================================================
    // PUBLIC API - COMPONENT ACCESS
    // ========================================================================

    public getSelectionManager(): SelectionManager<T> {
        return this.selectionManager;
    }

    public getColumnManager(): ColumnManager<T> {
        return this.columnManager;
    }

    public getDataSource(): DataSource<T> | LiveDataSource<T> {
        return this.dataSource;
    }

    public getRowStateManager(): RowStateManager {
        return this.rowStateManager;
    }

    // ========================================================================
    // Convenience Methods
    // ========================================================================

    /** Clear all selected items */
    public clearSelection(): void {
        this.selectionManager.clear();
    }

    /** Add a CSS class to a specific row/card by item key */
    public setRowClass(key: string, className: string): void {
        this.rowStateManager.addClass(key, className);
    }

    /** Remove a CSS class from a specific row/card by item key */
    public removeRowClass(key: string, className: string): void {
        this.rowStateManager.removeClass(key, className);
    }

    /** Set the active (highlighted) row/card key for master-detail patterns */
    public setActiveKey(key: string | null): void {
        if (this._activeKey === key) return;
        this._activeKey = key;
        this.scroller.scheduleUpdate();
    }

    /** Clear the active row highlight (e.g., when detail pane closes) */
    public clearActiveKey(): void {
        this.setActiveKey(null);
    }

    /** Re-render a single item without refreshing the DataSource */
    public updateItemDisplay(key: string): void {
        const index = this.dataSource.getIndexByKey(key);
        if (index < 0) return;
        const item = this.dataSource.getItemByKey(key);
        if (!item || !this.activeRenderer) return;
        this.activeRenderer.updateItem(index, item);
    }


    // ========================================================================
    // PUBLIC API - SCROLL CONTROL
    // ========================================================================

    public scrollToItem(item: T, align: 'start' | 'center' | 'end' = 'center'): void {
        const index = this.dataSource.getIndexByKey(this.keyFn(item));
        if (index >= 0) {
            this.scroller.scrollToIndex(index, align);
        }
    }

    public scrollToTop(): void {
        this.scroller.scrollToIndex(0, 'start');
    }

    public scrollToBottom(): void {
        const lastIndex = this.dataSource.getTotalCount() - 1;
        if (lastIndex >= 0) {
            this.scroller.scrollToIndex(lastIndex, 'end');
        }
    }

    // ========================================================================
    // PUBLIC API - FOCUS
    // ========================================================================

    public focus(): void {
        const idx = this.scroller.getFocusedIndex();
        const el = this.activeRenderer?.getItemElement(idx);
        el?.focus();
    }

    // ========================================================================
    // PUBLIC API - LIFECYCLE
    // ========================================================================

    public async refresh(): Promise<void> {
        await this.dataSource.refresh();
    }

    // ========================================================================
    // HELPER METHODS
    // ========================================================================

    private getEstimatedRowHeight(): number {
        if (this.state.viewMode === 'table') {
            return this.config.tableRowHeight ?? 36;
        }
        // Grid/thumbs mode - estimate based on card width
        return this.state.viewMode === 'thumbs'
            ? this.state.thumbSize
            : (this.config.cardWidth ?? 200);
    }

    private getColumnsPerRow(): number {
        if (this.state.viewMode === 'table') {
            return 1;
        }
        // Use GridRenderer's calculation if available
        if (this.gridRenderer) {
            return this.gridRenderer.getColumnsPerRow();
        }
        // Fallback calculation
        const containerWidth = this.scrollContainer.clientWidth;
        const gap = this.config.gridGap ?? 8;
        const cardWidth = this.state.viewMode === 'thumbs'
            ? this.state.thumbSize
            : (this.config.cardWidth ?? 200);

        return Math.max(1, Math.floor((containerWidth + gap) / (cardWidth + gap)));
    }

    private getVisibleItems(): T[] {
        const items: T[] = [];
        for (let i = this.state.renderedStartIndex; i <= this.state.renderedEndIndex; i++) {
            const item = this.dataSource.getItem(i);
            if (item) {
                items.push(item);
            }
        }
        return items;
    }

    private onColumnsChanged(): void {
        if (this.activeRenderer) {
            this.activeRenderer.onColumnsChanged();
        }
        this.persistState();
    }

    private isValidViewMode(mode: string): mode is ViewMode {
        const validModes = this.config.enabledModes ?? ['table', 'grid', 'thumbs'];
        return validModes.includes(mode as ViewMode);
    }

    // ========================================================================
    // PERSISTENCE
    // ========================================================================

    private persistState(): void {
        if (!this.persistenceKey) return;

        // Debounce persistence
        if (this.persistenceDebounceId) {
            clearTimeout(this.persistenceDebounceId);
        }

        this.persistenceDebounceId = setTimeout(() => {
            this.persistenceDebounceId = null;

            const storage = this.config.storage ?? localStorageProvider;
            const state: PersistedViewState = {
                viewMode: this.state.viewMode,
                thumbSize: this.state.thumbSize,
                sortColumn: this.state.sortColumn ?? undefined,
                sortDirection: this.state.sortDirection,
                visibleColumns: this.columnManager.getVisibleColumns().map(c => c.key),
                columnOrder: this.columnManager.getAllColumns().map(c => c.key),
                heatMapEnabled: this.state.heatMapEnabled,
                scrollTop: this.scroller.getScrollTop(),
            };

            saveViewState(this.persistenceKey!, state, storage);
        }, 500);
    }

    // ========================================================================
    // DISPOSE
    // ========================================================================

    public dispose(): void {
        // Clear persistence debounce
        if (this.persistenceDebounceId) {
            clearTimeout(this.persistenceDebounceId);
        }

        // Stop live data
        if (this.isLiveDataSource(this.dataSource)) {
            this.dataSource.stopListening();
        }

        // Dispose renderers
        this.disposeActiveRenderer();

        // Dispose all tracked disposables (includes all owned components)
        this.disposables.dispose();

        // Remove DOM
        this.rootElement.remove();
    }
}
